import { useState } from 'react';
import { UnitedStatesState } from '@shared';
import { ApplicationSectionFrame } from '../components/application-section-frame';
import { SelectField, TextField } from '../components/form-fields';
import { NumberField } from '../components/number-field';
import { useApplication } from '../hooks/use-application';
import type { ConsumerStepProps } from './step-props';

const STATE_OPTIONS = Object.values(UnitedStatesState)
  .filter(state => state.stateAbbreviation)
  .map(state => ({ value: state.stateAbbreviation, label: state.stateName }));

const SEX_OPTIONS = [
  { value: 'Female', label: 'Female' },
  { value: 'Male', label: 'Male' },
];

const MARITAL_OPTIONS = ['Single', 'Married', 'Domestic partnership', 'Divorced', 'Widowed'].map(value => ({
  value,
  label: value,
}));

export function ApplicationPersonalStep({ clientId }: ConsumerStepProps) {
  const { answers } = useApplication(clientId);
  const [personal, setPersonal] = useState(answers.personal);
  const set =
    <K extends keyof ConsumerApplicationPersonal>(field: K) =>
    (value: ConsumerApplicationPersonal[K]) =>
      setPersonal(current => ({ ...current, [field]: value }));

  return (
    <ApplicationSectionFrame
      clientId={clientId}
      section="personal"
      title="Your personal information"
      subtitle="We’ve filled in what you shared in your intake. Check it and add what’s missing."
      draft={{ personal }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="First name" value={personal.firstName} onChange={set('firstName')} autoComplete="given-name" />
        <TextField label="Last name" value={personal.lastName} onChange={set('lastName')} autoComplete="family-name" />
        <TextField label="Date of birth" type="date" value={personal.dateOfBirth} onChange={set('dateOfBirth')} />
        <SelectField label="Sex" value={personal.sex} onChange={set('sex')} options={SEX_OPTIONS} />
      </div>
      <TextField label="Street address" value={personal.street} onChange={set('street')} autoComplete="street-address" />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField label="City" value={personal.city} onChange={set('city')} autoComplete="address-level2" />
        <SelectField label="State" value={personal.state} onChange={set('state')} options={STATE_OPTIONS} />
        <TextField label="ZIP code" value={personal.zip} onChange={set('zip')} autoComplete="postal-code" maxLength={10} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="Phone" type="tel" value={personal.phone} onChange={set('phone')} autoComplete="tel" />
        <TextField label="Email" type="email" value={personal.email} onChange={set('email')} autoComplete="email" />
      </div>
      <SelectField label="Marital status" value={personal.maritalStatus} onChange={set('maritalStatus')} options={MARITAL_OPTIONS} />
      <div className="grid gap-4 sm:grid-cols-3">
        <NumberField label="Height (feet)" value={personal.heightFeet} onChange={set('heightFeet')} min={3} max={8} />
        <NumberField label="Height (inches)" value={personal.heightInches} onChange={set('heightInches')} min={0} max={11} />
        <NumberField label="Weight (lb)" value={personal.weightPounds} onChange={set('weightPounds')} min={50} max={600} />
      </div>
    </ApplicationSectionFrame>
  );
}
